import React from 'react';

const Editorial = () => {
  return (
    <main className="page editorial">
      <span className="h2__n">ESCALAS & TERMINOLOGIA</span>
      <h2 className="h2">O que é a Escala de Glasgow?</h2>
      <div className="atomic">
        <span className="atomic__lb mono">RESUMO</span>
        <p>A Escala de Coma de Glasgow descreve o nível de consciência por três componentes independentes: abertura ocular, resposta verbal e resposta motora. O registro por componentes preserva mais informação do que o total isolado.</p>
      </div>
      <div className="prose">
        <p>A ECG foi descrita por Graham Teasdale e Bryan Jennett em 1974. O método moderno recomenda observar, estimular quando necessário, classificar a melhor resposta observada e registrar fatores que interfiram na avaliação.</p>
        <p>Veja a <a href="/tabela">tabela completa</a> ou use a <a href="/#calculadora">calculadora</a>.</p>
      </div>

      <span className="h2__n">INTERPRETAÇÃO</span>
      <h2 className="h2">Como interpretar o escore da Escala de Glasgow?</h2>
      <div className="atomic">
        <span className="atomic__lb mono">RESUMO</span>
        <p>Quando os três componentes são testáveis, o total varia de 3 a 15. A leitura mais útil continua sendo O, V e M separadamente, porque o mesmo total pode resultar de combinações clínicas diferentes.</p>
      </div>
      <table className="tb tb--sev">
        <thead><tr><th>Escore</th><th>Faixa</th><th>Leitura</th></tr></thead>
        <tbody>
          <tr data-sev="leve"><td>13–15</td><td>Leve</td><td>Menor comprometimento do nível de consciência.</td></tr>
          <tr data-sev="moderado"><td>9–12</td><td>Moderado</td><td>Comprometimento intermediário; exige avaliação clínica e acompanhamento.</td></tr>
          <tr data-sev="grave"><td>3–8</td><td>Grave</td><td>Comprometimento importante; o número isolado não determina conduta de via aérea.</td></tr>
        </tbody>
      </table>

      <hr className="rule" />
      <span className="h2__n">AVALIAÇÃO</span>
      <h2 className="h2">Como avaliar os componentes?</h2>
      <div className="atomic">
        <span className="atomic__lb mono">RESUMO</span>
        <p>Observe primeiro. Se não houver resposta espontânea, use estímulo sonoro e depois pressão padronizada. Julgue ocular, verbal e motor separadamente e registre NT quando um fator de interferência impedir avaliação confiável.</p>
      </div>
      <div className="prose">
        <h3>Abertura ocular (O)</h3>
        <p>Observe abertura espontânea; depois use som e, se necessário, pressão. A terminologia moderna usa “pressão”, não “dor”, para padronizar a avaliação.</p>
        <h3>Resposta verbal (V)</h3>
        <p>Classifique orientação, confusão, palavras isoladas, sons ou ausência. Afasia, intubação e outros fatores podem tornar o componente não testável.</p>
        <h3>Resposta motora (M)</h3>
        <p>Teste comando simples e, se necessário, pressão. Diferencie localização, flexão normal, flexão anormal, extensão e ausência. Veja o passo a passo em <a href="/como-avaliar">Como avaliar</a>.</p>
      </div>

      <hr className="rule" />
      <span className="h2__n">PEDIÁTRICO</span>
      <h2 className="h2">Como aplicar a Escala de Glasgow em crianças?</h2>
      <div className="atomic">
        <span className="atomic__lb mono">RESUMO</span>
        <p>Em crianças pré-verbais, a estrutura da ECG é preservada e os descritores são adaptados ao desenvolvimento. Amplitudes idênticas à versão adulta: O 1–4 · V 1–5 · M 1–6 · total 3–15.</p>
      </div>
      <div className="prose">
        <p>O que muda são os descritores, não os limites. A resposta verbal passa a considerar interação, balbucio e padrão de choro; a resposta motora considera comportamento apropriado para a idade. A <a href="/pediatrica">página pediátrica</a> traz a tabela completa e uma calculadora específica com resultado.</p>
      </div>
      <table className="tb">
        <thead><tr><th>Pontos</th><th>Abertura ocular (O)</th><th>Resposta verbal (V)</th><th>Resposta motora (M)</th></tr></thead>
        <tbody>
          <tr><td>6</td><td>—</td><td>—</td><td>Movimenta-se espontânea e adequadamente para a idade (ou obedece)</td></tr>
          <tr><td>5</td><td>—</td><td>Balbucia, arrulha, sorriso social, fixa e segue objetos</td><td>Localiza: retira ao toque</td></tr>
          <tr><td>4</td><td>Espontânea</td><td>Choro irritado, mas consolável</td><td>Retirada à pressão (flexão normal)</td></tr>
          <tr><td>3</td><td>Ao som / à fala</td><td>Choro persistente, inconsolável; gemido à pressão</td><td>Flexão anormal (decorticação)</td></tr>
          <tr><td>2</td><td>À pressão</td><td>Agitação, inquietação inconsolável</td><td>Extensão (descerebração)</td></tr>
          <tr><td>1</td><td>Ausente</td><td>Ausente</td><td>Ausente</td></tr>
          <tr><td>NT</td><td>Edema periorbital, trauma ocular</td><td>Intubação, traqueostomia</td><td>Bloqueio neuromuscular, sedação profunda</td></tr>
        </tbody>
      </table>

      <span className="h2__n">NT</span>
      <h2 className="h2">Quando registrar um componente como Não Testável (NT)?</h2>
      <div className="atomic">
        <span className="atomic__lb mono">RESUMO</span>
        <p>Use NT quando um fator clínico impedir avaliação confiável. NT não equivale a 1. Se houver qualquer componente NT, não some o total; registre os componentes disponíveis e documente o fator de interferência.</p>
      </div>
      <table className="tb tb--algo">
        <thead><tr><th>Componente</th><th>Exemplo de limitante</th><th>Registro</th></tr></thead>
        <tbody>
          <tr><td>Ocular</td><td>Edema periorbital ou trauma ocular</td><td className="mono">O(NT) V5 M6</td></tr>
          <tr><td>Verbal</td><td>Intubação ou afasia que impeça avaliação confiável</td><td className="mono">O3 V(NT) M5</td></tr>
          <tr><td>Motor</td><td>Bloqueio neuromuscular ou sedação profunda</td><td className="mono">O4 V4 M(NT)</td></tr>
        </tbody>
      </table>

      <span className="h2__n">PROGRESSÃO</span>
      <h2 className="h2">Por que a evolução seriada importa?</h2>
      <div className="atomic">
        <span className="atomic__lb mono">RESUMO</span>
        <p>A ECG foi feita para comunicar o estado observado e sua evolução. Compare avaliações ao longo do tempo, sempre registrando horário, componentes e contexto, em vez de interpretar um único total sem referência temporal.</p>
      </div>

      <span className="h2__n">LIMITANTES</span>
      <h2 className="h2">Como avaliar Glasgow em paciente sedado, intubado ou afásico?</h2>
      <div className="atomic">
        <span className="atomic__lb mono">RESUMO</span>
        <p>Registre como NT somente o componente realmente inviabilizado. Intubação afeta o verbal; edema periorbital pode impedir o ocular; bloqueio neuromuscular pode impedir o motor. Não converta impossibilidade de teste em pontuação baixa.</p>
      </div>
      <div className="prose">
        <h3>Paciente intubado</h3><p>Registre V(NT) e mantenha O e M quando testáveis.</p>
        <h3>Paciente com afasia expressiva</h3><p>Afasia pode preservar compreensão sem permitir produção verbal. Não compense compreensão com V4 ou V5; se a avaliação verbal não for confiável, registre V(NT) e descreva a compreensão em texto livre.</p>
        <h3>Bloqueio neuromuscular</h3><p>Se o movimento não pode ser julgado, registre M(NT) e documente o fator de interferência.</p>
      </div>
    </main>
  );
};

export default Editorial;